import { Card, ErrorBox, Loading, Empty } from './ui'
import DailyChart from './DailyChart'
import WeekdayTable from './WeekdayTable'
import { money, num, negClass, dayLabel, safeDiv, isNum } from './format'

const th = 'text-right font-semibold text-xs text-slate-500 px-2.5 py-2 whitespace-nowrap'
const td = 'text-right px-2.5 py-1.5 tabular-nums whitespace-nowrap'

/**
 * query = usePnlDaily(from, to). periodLabel e.g. 'October 2026'.
 * targetPbitMonth: settings target, drawn as a per-working-day line when the
 * period is a single month.
 */
export default function DailyTab({ query, periodLabel, isMonth, targetPbitMonth }) {
  const { data, error, isLoading } = query
  if (error) return <ErrorBox error={error} what="daily figures" />
  if (isLoading && !data) return <Card title="Profit per day"><Loading /></Card>
  const all = data?.days || []
  // Weekends and public holidays have no orders — hide them, but never hide a
  // day that did ship something.
  const days = all.filter((d) => d.is_working_day || Number(d.orders) > 0)
  if (!days.length) return <Card title={`Profit per day, ${periodLabel}`}><Empty>No working days in this period yet.</Empty></Card>

  const workingDays = all.filter((d) => d.is_working_day).length
  const targetPerDay = isMonth && isNum(targetPbitMonth) && workingDays ? Number(targetPbitMonth) / workingDays : null
  const t = data.totals || {}
  const costPerOrder = isNum(t.cost_per_order)
    ? t.cost_per_order
    : safeDiv(isNum(t.labour_cost) && isNum(t.overheads) ? Number(t.labour_cost) + Number(t.overheads) : null, t.orders)

  return (
    <>
      <Card title={`Profit per day, ${periodLabel}`} sub="Working days only. Hover a bar for the detail.">
        <div className="flex flex-wrap gap-3.5 text-xs text-slate-500 mb-2 items-center">
          <span><span className="inline-block w-2.5 h-2.5 rounded-sm mr-1.5 align-[-1px] bg-emerald-500" />profit (left axis)</span>
          <span><span className="inline-block w-2.5 h-2.5 rounded-sm mr-1.5 align-[-1px] bg-red-600" />loss</span>
          <span><span className="inline-block w-2.5 h-2.5 rounded-sm mr-1.5 align-[-1px] bg-blue-600" />orders shipped (right axis)</span>
          {targetPerDay !== null && <span className="text-emerald-700">- - target per working day {money(targetPerDay)}</span>}
        </div>
        <DailyChart days={days} targetPerDay={targetPerDay} />
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card
          title="For rostering: average by weekday"
          sub="Break-even orders = orders needed that day to cover the hours you rostered plus that day's share of overheads."
        >
          <WeekdayTable weekdays={data.weekdays} />
        </Card>
        <Card title="How a day's profit is worked out">
          <ul className="list-disc pl-5 text-[12.5px] text-slate-700 leading-relaxed space-y-0.5">
            <li><b>Revenue:</b> that day&apos;s shipped orders, plus storage and other monthly charges spread across the working days.</li>
            <li><b>Carrier costs:</b> each order&apos;s postage, ex-GST.</li>
            <li><b>Labour:</b> hours on that day&apos;s Xero timesheets × the month&apos;s loaded pay rate (wages + super ÷ timesheet hours).</li>
            <li><b>Overheads:</b> the month&apos;s rent, software, insurance and so on, spread evenly over its working days.</li>
            <li>Weekends and public holidays have no orders, so they are not shown.</li>
          </ul>
        </Card>
      </div>

      <Card title="Every day">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-slate-300">
                <th className={`${th} text-left`}>Day</th>
                <th className={th}>Orders</th>
                <th className={th}>Revenue</th>
                <th className={th}>Carrier &amp; direct costs</th>
                <th className={th}>Labour hrs</th>
                <th className={th}>Labour cost</th>
                <th className={th}>Overheads</th>
                <th className={th}>Profit</th>
                <th className={th}>Wages + overheads per order</th>
                <th className={th}>Break-even orders</th>
              </tr>
            </thead>
            <tbody>
              {days.map((d) => (
                <tr key={d.date} className="border-b border-slate-100">
                  <td className={`${td} text-left`}>{dayLabel(d.date)}{!d.is_working_day && <span className="text-xs text-slate-400 ml-1">(non-working)</span>}</td>
                  <td className={td}>{num(d.orders)}</td>
                  <td className={td}>{money(d.revenue)}</td>
                  <td className={td}>{money(d.direct_costs)}</td>
                  <td className={td}>{num(d.labour_hours, 1)}</td>
                  <td className={td}>{money(d.labour_cost)}</td>
                  <td className={td}>{money(d.overheads)}</td>
                  <td className={`${td} font-semibold ${negClass(d.profit)}`}>{money(d.profit)}</td>
                  <td className={td}>{money(d.cost_per_order, 2)}</td>
                  <td className={td}>{num(d.breakeven_orders)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-300 font-bold">
                <td className={`${td} text-left`}>{periodLabel}</td>
                <td className={td}>{num(t.orders)}</td>
                <td className={td}>{money(t.revenue)}</td>
                <td className={td}>{money(t.direct_costs)}</td>
                <td className={td}>{num(t.labour_hours, 0)}</td>
                <td className={td}>{money(t.labour_cost)}</td>
                <td className={td}>{money(t.overheads)}</td>
                <td className={`${td} ${negClass(t.profit)}`}>{money(t.profit)}</td>
                <td className={td}>{money(costPerOrder, 2)}</td>
                <td className={td} />
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>
    </>
  )
}
